import { useCallback, useEffect, useState } from 'react'

// Bump when the tour content changes enough to warrant re-showing it once to
// everyone. The flag is namespaced per account (see STORAGE_KEY) so two users
// sharing a browser each get their own first-usage tour.
export const ONBOARDING_TOUR_VERSION = '1'

const STORAGE_KEY = (accountId: number | undefined) =>
  `autoriz.onboarding.tour.seen.v${ONBOARDING_TOUR_VERSION}.${accountId ?? 'anon'}`

/**
 * Tracks whether {@link accountId} has already seen the first-usage guided
 * tour. Mirrors {@link useChangelogSeen}'s localStorage pattern (SSR guard +
 * try/catch): `seen` starts `true` so nothing flashes on first render, and the
 * effect flips it to `false` only when no flag is stored yet — which is what
 * lets the tour auto-open exactly once for a brand-new salesman or company.
 *
 * With no `accountId` (identity not resolved yet) it stays `seen` and never
 * auto-opens.
 */
export const useOnboardingTourSeen = (accountId: number | undefined) => {
  const [seen, setSeen] = useState<boolean>(true)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (accountId == null) return
    try {
      if (!window.localStorage.getItem(STORAGE_KEY(accountId))) setSeen(false)
    } catch {
      // localStorage unavailable — treat as already seen, never auto-open
    }
  }, [accountId])

  const markSeen = useCallback(() => {
    setSeen(true)
    if (typeof window === 'undefined') return
    if (accountId == null) return
    try {
      window.localStorage.setItem(STORAGE_KEY(accountId), '1')
    } catch {
      // ignore
    }
  }, [accountId])

  return { seen, markSeen }
}
